import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Clock } from 'lucide-react'
import { EmptyState, StatusBadge, TableRowActions, SortableTh, TableFilterBar, TablePagination } from '@/shared/components'
import { useActions, useDeleteAction } from '../hooks/use-actions'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { useTableSort } from '@/shared/hooks/use-table-sort'
import { usePagination } from '@/shared/hooks/use-pagination'
import { cn } from '@/lib/utils'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}min`
  if (m === 0) return `${h}h`
  return `${h}h ${m}min`
}

export function ActionList() {
  const { t } = useTranslation()
  const { data: actions, isLoading } = useActions()
  const deleteAction = useDeleteAction()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [statusFilter, setStatusFilter] = useState('all')

  const filtered = useMemo(() => {
    if (!actions) return []
    if (statusFilter === 'actif') return actions.filter((a) => a.actif)
    if (statusFilter === 'inactif') return actions.filter((a) => !a.actif)
    return actions
  }, [actions, statusFilter])

  const { sortedData, sortKey, sortDirection, handleSort } = useTableSort(filtered, 'libelle')
  const { paginatedData, currentPage, totalPages, totalItems, pageSize, setCurrentPage } = usePagination(sortedData)

  const [deleteDialog, setDeleteDialog] = useState<{ id: number; label: string } | null>(null)

  const handleDelete = (id: number, libelle: string) => {
    setDeleteDialog({ id, label: libelle })
  }

  const handleDeleteConfirm = () => {
    if (deleteDialog) {
      deleteAction.mutate(deleteDialog.id)
      setDeleteDialog(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (!actions || actions.length === 0) {
    return <EmptyState message={t('administration.sections.actions.noActions', 'Aucune action disponible. Créez votre première action !')} />
  }

  const counts = {
    all: actions.length,
    actif: actions.filter((a) => a.actif).length,
    inactif: actions.filter((a) => !a.actif).length,
  }

  return (
    <>
      <TableFilterBar
        filters={[{
          key: 'status',
          label: 'Statut',
          value: statusFilter,
          options: [
            { value: 'all', label: 'Tous', count: counts.all },
            { value: 'actif', label: 'Actifs', count: counts.actif },
            { value: 'inactif', label: 'Inactifs', count: counts.inactif },
          ],
        }]}
        onFilterChange={(_key, value) => setStatusFilter(value)}
      />
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-200 bg-gradient-to-r from-primary/5 to-transparent">
              <SortableTh label="Libellé" sortKey="libelle" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Durée" sortKey="dureeMinutes" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Statut" sortKey="actif" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-neutral-400" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {paginatedData.map((action) => (
              <tr key={action.id} className={cn('transition-colors hover:bg-neutral-50', !action.actif && 'opacity-50')}>
                <td className="px-4 py-3 text-sm font-medium text-neutral-900">
                  <span className="flex items-center gap-2">
                    {action.couleur && (
                      <span
                        className="inline-block h-3 w-3 shrink-0 rounded-full border border-neutral-200"
                        style={{ backgroundColor: action.couleur }}
                      />
                    )}
                    {action.libelle}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-neutral-500">
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 text-neutral-400" />
                    {formatDuration(action.dureeMinutes)}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    {action.actif
                      ? <StatusBadge label="Actif" variant="active" />
                      : <StatusBadge label="Inactif" variant="inactive" />}
                    {action.estSysteme && <StatusBadge label="Système" variant="system" />}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  {(!action.estSysteme || isAdmin) && (
                    <TableRowActions
                      editTo={`/administration/actions/${action.id}`}
                      onDelete={() => handleDelete(action.id, action.libelle)}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize} onPageChange={setCurrentPage} />
      </div>

      <ConfirmDialog
        open={!!deleteDialog}
        variant="danger"
        title={`Supprimer l'action "${deleteDialog?.label}" ?`}
        description="Cette action est irréversible."
        confirmLabel="Supprimer"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog(null)}
      />
    </>
  )
}
